import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database, Json } from '@/types/database.generated';

export type StaffNotification = Database['public']['Tables']['notifications']['Row'];

export const staffNotificationsQueryKey = (clinicId: string | null | undefined) =>
  ['staff-notifications', clinicId ?? null] as const;

export function useStaffNotifications(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: staffNotificationsQueryKey(clinicId),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<StaffNotification[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('clinic_id', clinicId)
        .order('created_at', { ascending: false })
        .limit(40);
      if (error) throw error;
      return (data ?? []) as StaffNotification[];
    },
    staleTime: 8_000,
  });
}

export function useMarkStaffNotificationRead(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: staffNotificationsQueryKey(clinicId) });
    },
  });
}

export function useStaffNotificationsRealtime(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!clinicId) return;
    const channel = supabase
      .channel(`notifications:${clinicId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `clinic_id=eq.${clinicId}`,
        },
        () => {
          void qc.invalidateQueries({ queryKey: staffNotificationsQueryKey(clinicId) });
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [clinicId, qc]);
}

export function notificationMetadata(value: Json): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value as Record<string, unknown>;
}
